// src/components/PortalAfiliado/DashboardAfiliado.tsx
//
// UPDATED: 2026-04-27
// ✅ certificadosEmitidos conta alunos com status 'graduated'
// ✅ Atividades recentes incluem formandos com generatedId
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  AlertCircle, Activity, Loader2, RefreshCw,
  UserPlus, BookOpen, FolderOpen, CalendarCheck, ArrowRight,
} from 'lucide-react';
import { studentService } from '../../services/studentService';
import { affiliateService } from '../../services/affiliateService';
import { updateUser } from '../../store/slices/authSlice';

// ── Redux ─────────────────────────────────────────────────────────────────────
interface RootState {
  auth: {
    user?: {
      affiliateId?: string;
      schoolName?:  string;
      fullName?:    string;
    };
  };
}

interface DashStats {
  totalAlunos:          number;
  alunosAtivos:         number;
  cursosAtivos:         number;
  certificadosEmitidos: number;
}

interface Atividade {
  id:       string;
  mensagem: string;
  tempo:    string;
  icon:     string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function timeAgo(dateStr: string): string {
  const diff  = Date.now() - new Date(dateStr).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(mins / 60);
  const days  = Math.floor(hours / 24);
  if (days  > 0) return `${days}d atrás`;
  if (hours > 0) return `${hours}h atrás`;
  if (mins  > 0) return `${mins}min atrás`;
  return 'agora mesmo';
}

// ── Componente ────────────────────────────────────────────────────────────────
const DashboardAfiliado: React.FC<{
  onCadastrarClick?:  () => void;
  onCursosClick?:     () => void;
  onDocumentosClick?: () => void;
  onRenovacaoClick?:  () => void;
}> = ({ onCadastrarClick, onCursosClick, onDocumentosClick, onRenovacaoClick }) => {

const dispatch     = useDispatch();
  const affiliateId  = useSelector((s: RootState) => s.auth.user?.affiliateId ?? '');
  const schoolName   = useSelector((s: RootState) => s.auth.user?.schoolName  ?? '');
  // ✅ useState com valores iniciais corretos — sem referência a variáveis externas
  const [stats, setStats] = useState<DashStats>({
    totalAlunos:          0,
    alunosAtivos:         0,
    cursosAtivos:         0,
    certificadosEmitidos: 0,
  });
  const [atividades, setAtividades] = useState<Atividade[]>([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);

  // ── Fetch ─────────────────────────────────────────────────────────────────
 const fetchDashboard = useCallback(async () => {
    if (!affiliateId) {
      setLoading(false);
      setError('Não foi possível identificar o filiado desta sessão. Entre novamente.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // ✅ Busca os dados atuais do afiliado (nome, logo, status) direto do banco —
      // evita exibir o "retrato" salvo no Redux desde o login
      try {
        const fresh = await affiliateService.getMyAffiliate();
        dispatch(updateUser({
          schoolName: fresh.schoolName,
          logoUrl:    fresh.logoUrl,
          status:     fresh.status,
        }));
      } catch {
        // se /affiliates/me falhar, segue com o que já está no Redux
      }

      const res    = await studentService.listStudents({ affiliateId, page: 1, limit: 50 });
      const alunos = res.students ?? [];

      // ✅ certificadosEmitidos conta formados reais
      setStats({
        totalAlunos:          res.total ?? alunos.length,
        alunosAtivos:         alunos.filter(s => s.status === 'active').length,
        cursosAtivos:         0,
        certificadosEmitidos: alunos.filter(s => s.status === 'graduated').length, // ✅ CORRIGIDO
      });

      // ── Atividades: formados + cadastros recentes ────────────────────────
      // [NOVO] formados com generatedId e registroFepcssp
      const formados: Atividade[] = alunos
        .filter(s => s.status === 'graduated')
        .sort((a, b) =>
          new Date((b as any).updatedAt ?? b.createdAt).getTime() -
          new Date((a as any).updatedAt ?? a.createdAt).getTime()
        )
        .slice(0, 3)
        .map(s => {
          const req = (s as any).diplomaRequests
            ?.filter((r: any) => r.status === 'approved')
            .at(-1);
          const roleLabel = req?.promotedAs === 'professional' ? 'Profissional'
                          : req?.promotedAs === 'affiliate'    ? 'Filiado'
                          : 'Formado';
          return {
            id:       s.id + '-grad',
            mensagem: `${s.fullName} formado como ${roleLabel}${req?.generatedId ? ` · ${req.generatedId}` : ''}`,
            tempo:    timeAgo((s as any).updatedAt ?? s.createdAt),
            icon:     '🎓',
          };
        });

      const recentes: Atividade[] = [...alunos]
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 5)
        .map(s => ({
          id:       s.id,
          mensagem: `Novo aluno ${s.fullName} cadastrado${s.courseName ? ` — ${s.courseName}` : ''}`,
          tempo:    timeAgo(s.createdAt),
          icon:     '👤',
        }));

      // Mescla formados (topo) com recentes, sem duplicar, limita a 5
      const idsFormados = new Set(formados.map(f => f.id));
      const merged = [
        ...formados,
        ...recentes.filter(r => !idsFormados.has(r.id + '-grad')),
      ].slice(0, 5);

      setAtividades(merged);

    } catch (err: any) {
      setError(err?.message ?? 'Erro ao carregar dashboard');
    } finally {
      setLoading(false);
    }
}, [affiliateId, dispatch]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  // ── Stat cards ────────────────────────────────────────────────────────────
  const statCards = [
    { label: 'Total de Alunos',       value: stats.totalAlunos,          iconPath: '/icons/icon.png',   dot: '#3b82f6', sub: 'matriculados' },
    { label: 'Alunos Ativos',         value: stats.alunosAtivos,         iconPath: '/icons/b9.png',     dot: '#22c55e', sub: 'em cursos'    },
    { label: 'Cursos Ativos',         value: stats.cursosAtivos,         iconPath: '/icons/course.png', dot: '#d4af37', sub: 'homologados'  },
    { label: 'Certificados Emitidos', value: stats.certificadosEmitidos, iconPath: '/icons/icon8.png',  dot: '#a855f7', sub: 'total'        },
  ];

  const quickActions = [
    { id: 'cadastrar', label: 'Cadastrar Aluno',  Icon: UserPlus,      desc: 'Adicionar novo aluno ao sistema', iconBg: 'bg-blue-100',    iconColor: 'text-blue-600',    cardBg: 'bg-blue-50   hover:bg-blue-100',    border: 'border-blue-200',    onClick: onCadastrarClick  },
    { id: 'cursos',    label: 'Gerenciar Cursos', Icon: BookOpen,      desc: 'Ver e editar seus cursos',        iconBg: 'bg-amber-100',   iconColor: 'text-amber-600',   cardBg: 'bg-amber-50  hover:bg-amber-100',   border: 'border-amber-200',   onClick: onCursosClick     },
    { id: 'docs',      label: 'Base Didática',    Icon: FolderOpen,    desc: 'Acessar materiais e documentos',  iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600', cardBg: 'bg-emerald-50 hover:bg-emerald-100', border: 'border-emerald-200', onClick: onDocumentosClick },
    { id: 'renovar',   label: 'Renovação Anual',  Icon: CalendarCheck, desc: 'Renovar sua filiação',            iconBg: 'bg-purple-100',  iconColor: 'text-purple-600',  cardBg: 'bg-purple-50 hover:bg-purple-100',  border: 'border-purple-200',  onClick: onRenovacaoClick  },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-7">

      {/* HEADER */}
      <div className="flex items-center justify-between gap-3.5">
        <div className="flex items-center gap-3.5">
          <div className="relative flex-shrink-0">
            <div className="absolute inset-0 bg-gold-400/20 blur-[10px] rounded-full" />
            <img src="/icons/dash.png" alt="Dashboard"
              className="relative w-11 h-11 object-contain drop-shadow" />
          </div>
          <div>
            <h1 className="text-[22px] font-semibold text-gray-900 tracking-tight leading-none">
              Visão Geral
            </h1>
            <p className="text-xs text-gray-400 mt-1 tracking-wide">
              {schoolName || 'Gerencie alunos, cursos e diplomas'}
            </p>
          </div>
        </div>

        <button onClick={fetchDashboard} disabled={loading}
          className="p-2.5 rounded-xl border border-gray-200 text-gray-500
            hover:bg-gray-50 transition-colors disabled:opacity-50 flex-shrink-0">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* ERROR */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-red-50 border border-red-200">
          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700 flex-1">{error}</p>
          <button onClick={fetchDashboard}
            className="text-xs text-red-600 font-medium hover:underline">
            Tentar novamente
          </button>
        </div>
      )}

      {/* STAT CARDS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map(({ label, value, iconPath, dot, sub }) => (
          <div key={label}
            className="bg-white rounded-2xl border border-gray-100 p-5
              shadow-[0_1px_8px_rgba(0,0,0,0.05)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.08)]
              transition-shadow duration-200">
            <div className="flex items-start justify-between mb-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ background: dot + '15' }}>
                <img src={iconPath} alt={label} className="w-4.5 h-4.5 object-contain"
                  onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
              </div>
              <span className="w-2 h-2 rounded-full mt-1 flex-shrink-0"
                style={{ background: dot }} />
            </div>
            {loading
              ? <div className="h-8 w-12 rounded-lg bg-gray-100 animate-pulse mb-1" />
              : <p className="text-2xl font-semibold text-gray-900 leading-none">{value}</p>
            }
            <p className="text-xs text-gray-500 mt-1.5 leading-tight">{label}</p>
            <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>
          </div>
        ))}
      </div>

      {/* QUICK ACTIONS + ATIVIDADES */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl border border-gray-100
          shadow-[0_1px_8px_rgba(0,0,0,0.05)] p-5">
          <div className="flex items-center gap-2 mb-4">
            <p className="text-sm font-bold text-gray-800 tracking-tight">Ações Rápidas</p>
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">
              — o que deseja fazer?
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {quickActions.map(action => (
              <button key={action.id} onClick={action.onClick}
                className={`flex flex-col items-start gap-3 p-4 rounded-2xl border
                  ${action.cardBg} ${action.border}
                  transition-all duration-200 text-left group
                  hover:shadow-md active:scale-[0.98]`}>
                <div className={`w-10 h-10 rounded-xl ${action.iconBg} flex items-center justify-center`}>
                  <action.Icon className={`w-5 h-5 ${action.iconColor}`} />
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-bold ${action.iconColor} leading-tight`}>
                    {action.label}
                  </p>
                  <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">{action.desc}</p>
                </div>
                <ArrowRight className={`w-3.5 h-3.5 ${action.iconColor} opacity-50
                  group-hover:opacity-100 group-hover:translate-x-0.5 transition-all self-end`} />
              </button>
            ))}
          </div>
        </div>

        {/* Atividades recentes */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-100
          shadow-[0_1px_8px_rgba(0,0,0,0.05)] p-5">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">
              Atividade Recente
            </p>
            <Activity className="w-4 h-4 text-gray-300" />
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-10 gap-3">
              <Loader2 className="w-5 h-5 text-gold-500 animate-spin" />
              <p className="text-xs text-gray-400">Carregando…</p>
            </div>
          ) : atividades.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center mb-3">
                <AlertCircle className="w-5 h-5 text-gray-300" />
              </div>
              <p className="text-sm text-gray-500 font-medium">Nenhuma atividade recente</p>
              <p className="text-xs text-gray-400 mt-1">Comece cadastrando um aluno!</p>
            </div>
          ) : (
            <div className="space-y-0.5">
              {atividades.map((a, idx) => (
                <div key={a.id}
                  className={`flex items-start gap-3 px-3 py-2.5 rounded-xl
                    ${idx % 2 === 0 ? 'bg-gray-50/60' : ''}`}>
                  <span className="text-base leading-none mt-0.5 flex-shrink-0">{a.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-700 leading-snug truncate">{a.mensagem}</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">{a.tempo}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardAfiliado;
